import { Euler, EventDispatcher, MathUtils, Quaternion, Vector3 } from "three";

const _zee = new Vector3(0, 0, 1);
const _euler = new Euler();
const _q0 = new Quaternion();
const _q1 = new Quaternion(-Math.sqrt(0.5), 0, 0, Math.sqrt(0.5));

const _changeEvent = { type: "change" };

const EPS = 0.000001;
const lastQuaternion = new Quaternion();

/**
 *
 */
class DeviceOrientationController extends EventDispatcher {
  /**
   *
   * @param {*} object
   */
  constructor(object) {
    super();

    if (window.isSecureContext === false) {
      console.error(
        "THREE.DeviceOrientationControls: DeviceOrientationEvent is only available in secure contexts (https)"
      );
    }

    this.enabled = true;
    this.object = object;
    this.object.rotation.reorder("YXZ");

    this.deviceOrientation = {};
    this.screenOrientation = 0;

    this.alphaOffset = 0;

    this.TWO_PI = 2 * Math.PI;
    this.HALF_PI = 0.5 * Math.PI;
    this.orientationChangeEventName =
      "ondeviceorientationabsolute" in window ? "deviceorientationabsolute" : "deviceorientation";

    this.smoothingFactor = 1;
  }

  update() {
    if (this.enabled === false) return;

    const device = this.deviceOrientation;
    if (device) {
      let alpha = device.alpha ? MathUtils.degToRad(device.alpha) + this.alphaOffset : 0;
      let beta = device.beta ? MathUtils.degToRad(device.beta) : 0;
      let gamma = device.gamma ? MathUtils.degToRad(device.gamma) : 0;
      const orient = this.screenOrientation !== 0 ? MathUtils.degToRad(this.screenOrientation) : 0;

      if (this.smoothingFactor < 1) {
        if (this.lastOrientation) {
          const k = this.smoothingFactor;
          alpha = this._getSmoothedAngle(alpha, this.lastOrientation.alpha, k);
          beta = this._getSmoothedAngle(beta + Math.PI, this.lastOrientation.beta, k);
          gamma = this._getSmoothedAngle(gamma + this.HALF_PI, this.lastOrientation.gamma, k, Math.PI);
        } else {
          beta += Math.PI;
          gamma += this.HALF_PI;
        }

        this.lastOrientation = {
          alpha: alpha,
          beta: beta,
          gamma: gamma
        };
      }

      this._setObjectQuaternion(
        this.object.quaternion,
        alpha,
        this.smoothingFactor < 1 ? beta - Math.PI : beta,
        this.smoothingFactor < 1 ? gamma - this.HALF_PI : gamma,
        orient
      );

      if (8 * (1 - lastQuaternion.dot(this.object.quaternion)) > EPS) {
        lastQuaternion.copy(this.object.quaternion);
        this.dispatchEvent(_changeEvent);
      }
    }
  }

  async connect() {
    this._onScreenOrientationChangeEvent();

    if (DeviceOrientationEvent && DeviceOrientationEvent.requestPermission) {
      try {
        const res = await DeviceOrientationEvent.requestPermission();
        if (res === "granted") {
          this._listenOrientationEvents();
        }
      } catch (err) {
        console.error("Unable to use DeviceOrientation API");
      }
    } else {
      this._listenOrientationEvents();
    }
    this.enabled = true;
  }

  disconnect() {
    window.removeEventListener("orientationchange", this._onScreenOrientationChangeEvent);
    window.removeEventListener(this.orientationChangeEventName, this._onDeviceOrientationChangeEvent);
    this.enabled = false;
  }

  dispose() {
    this.disconnect();
  }

  // private functions

  _orderAngle(a, b, range = this.TWO_PI) {
    if ((b > a && Math.abs(b - a) < range / 2) || (a > b && Math.abs(b - a) > range / 2)) {
      return { left: a, right: b };
    } else {
      return { left: b, right: a };
    }
  }

  _onDeviceOrientationChangeEvent(event) {
    window.dispatchEvent(new CustomEvent("deviceorientationchange", { detail: event }));
    this.deviceOrientation = event;
  }

  _onScreenOrientationChangeEvent() {
    this.screenOrientation = window.orientation || 0;
  }

  _setObjectQuaternion(quaternion, alpha, beta, gamma, orient) {
    _euler.set(beta, alpha, -gamma, "YXZ");
    quaternion.setFromEuler(_euler);
    quaternion.multiply(_q1);
    quaternion.multiply(_q0.setFromAxisAngle(_zee, -orient));
  }

  _listenOrientationEvents() {
    window.addEventListener("orientationchange", this._onScreenOrientationChangeEvent.bind(this));
    window.addEventListener(this.orientationChangeEventName, this._onDeviceOrientationChangeEvent.bind(this));
  }

  _getSmoothedAngle(a, b, k, range = this.TWO_PI) {
    const angles = this._orderAngle(a, b, range);
    const angleshift = angles.left;
    const origAnglesRight = angles.right;
    angles.left = 0;
    angles.right -= angleshift;
    if (angles.right < 0) angles.right += range;
    let newangle =
      origAnglesRight == b ? (1 - k) * angles.right + k * angles.left : k * angles.right + (1 - k) * angles.left;
    newangle += angleshift;
    if (newangle >= range) newangle -= range;
    return newangle;
  }
}

export { DeviceOrientationController };
